import { logger } from "@/utils/logger";
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowRight, Loader2, MessageSquare } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useDealerSession } from '@/contexts/DealerSessionContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { getStoredInterestData } from '@/utils/interestDataUtils';
import { sessionManager } from '@/utils/sessionManager';
import { getUTMSource, getUTMCampaign } from '@/utils/cookieUtils';

interface SMSScreenshotModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  screenshotUrl: string;
}

const SMSScreenshotModal: React.FC<SMSScreenshotModalProps> = ({
  open,
  onOpenChange,
  screenshotUrl,
}) => {
  const { t, language } = useLanguage();
  const { sessionData } = useDealerSession();
  const [phone, setPhone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneError, setPhoneError] = useState('');

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setPhone('');
      setPhoneError('');
    }
  }, [open]);

  // Format phone number as user types (xxx-xxx-xxxx)
  const formatPhoneInput = (value: string): string => {
    // Remove all non-digits
    let digits = value.replace(/\D/g, '');
    
    // Strip leading "1" country code if present (11 digits starting with 1)
    if (digits.length === 11 && digits.startsWith('1')) {
      digits = digits.slice(1);
    }
    
    // Limit to 10 digits
    const limited = digits.slice(0, 10);
    
    // Format as xxx-xxx-xxxx
    if (limited.length <= 3) {
      return limited;
    } else if (limited.length <= 6) {
      return `${limited.slice(0, 3)}-${limited.slice(3)}`;
    } else {
      return `${limited.slice(0, 3)}-${limited.slice(3, 6)}-${limited.slice(6)}`;
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneInput(e.target.value);
    setPhone(formatted);
    
    // Clear error when user starts typing
    if (phoneError) {
      setPhoneError('');
    }
  };

  const validatePhone = (): boolean => {
    const digits = phone.replace(/\D/g, '');
    
    if (digits.length !== 10) {
      setPhoneError(language === 'fr' 
        ? 'Veuillez entrer un num&eacute;ro de t&eacute;l&eacute;phone valide &agrave; 10 chiffres'
        : 'Please enter a valid 10-digit phone number');
      return false;
    }
    
    return true;
  };

  // Get vehicle data from multiple sources with fallbacks
  const getVehicleData = async () => {
    // 1. First try stored interest data
    const interestData = await getStoredInterestData();
    if (interestData?.vehicle?.make || interestData?.vehicle?.model) {
      return interestData.vehicle;
    }
    
    // 2. Try Website_Current_Vehicle from localStorage
    try {
      const websiteVehicle = localStorage.getItem('Website_Current_Vehicle');
      if (websiteVehicle) {
        const parsed = JSON.parse(websiteVehicle);
        if (parsed?.make || parsed?.model) {
          logger.log('📱 SMS: Using Website_Current_Vehicle:', parsed);
          return {
            make: parsed.make,
            model: parsed.model,
            year: parsed.year,
            stock: parsed.stock || parsed.stockNumber,
            vin: parsed.vin || parsed.VIN,
          };
        }
      }
    } catch (e) {
      logger.warn('📱 SMS: Could not parse Website_Current_Vehicle', e);
    }
    
    // 3. Try VDPjsonData from localStorage
    try {
      const vdpData = localStorage.getItem('VDPjsonData');
      if (vdpData) {
        const parsed = JSON.parse(vdpData);
        if (parsed?.make || parsed?.model) {
          logger.log('📱 SMS: Using VDPjsonData:', parsed);
          return {
            make: parsed.make,
            model: parsed.model,
            year: parsed.year,
            stock: parsed.stock || parsed.stockNumber,
            vin: parsed.vin || parsed.VIN,
          };
        }
      }
    } catch (e) {
      logger.warn('📱 SMS: Could not parse VDPjsonData', e);
    }
    
    logger.log('📱 SMS: No vehicle data found in any source');
    return null;
  };

  const handleSubmit = async () => {
    if (!validatePhone()) return;
    
    setIsSubmitting(true);
    
    try {
      // Get vehicle data with fallbacks
      const vehicleData = await getVehicleData();
      
      // Format phone number for API (remove dashes)
      const formattedPhone = phone.replace(/\D/g, '');
      
      // Build message - use conversation summary if available, otherwise fallback
      const conversationSummary = sessionManager.getCurrentSession()?.conversationSummary;
      const message = conversationSummary || (language === 'fr'
        ? 'Voici une copie de votre conversation.'
        : 'Here is a copy of your conversation.');

      // Build request body, only including non-empty values
      const requestBody: Record<string, string> = {
        toNumber: formattedPhone,
        message,
        dealerId: sessionData?.dealerId?.toString() || '2',
        lang: language === 'fr' ? 'fr' : 'en',
        leadType: 'D2C_Chatbot_SMS',
      };

      // Only add vehicle params if they have values
      if (vehicleData?.make) requestBody.make = vehicleData.make;
      if (vehicleData?.model) requestBody.model = vehicleData.model;
      if (vehicleData?.year) requestBody.year = vehicleData.year.toString();
      if (vehicleData?.stock) requestBody.stockNumber = vehicleData.stock;
      if (vehicleData?.vin) requestBody.vin = vehicleData.vin;
      if (screenshotUrl) requestBody.attach = screenshotUrl;
      
      // Add campaign data from UTM cookies (ignore Google organic)
      const utmSource = getUTMSource();
      const utmCampaign = getUTMCampaign();
      const isGoogleOrganic = utmSource?.toLowerCase() === 'google' && utmCampaign?.toLowerCase() === 'organic';
      if (utmSource && !isGoogleOrganic) requestBody.campaignSource = utmSource;
      if (utmCampaign && !isGoogleOrganic) requestBody.campaignName = utmCampaign;

      const { data, error } = await supabase.functions.invoke('send-sms-through-d2c', {
        body: requestBody
      });

      if (error) throw error;

      if (data?.success) {
        toast.success(
          language === 'fr' 
            ? 'SMS envoy&eacute; avec succ&egrave;s!' 
            : 'SMS sent successfully!'
        );
        onOpenChange(false);
      } else {
        throw new Error(data?.error || 'SMS sending failed');
      }
    } catch (error) {
      console.error('SMS sending error:', error);
      toast.error(
        language === 'fr' 
          ? 'Erreur lors de l\'envoi du SMS' 
          : 'Error sending SMS'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isSubmitting && phone.replace(/\D/g, '').length === 10) {
      handleSubmit();
    }
  };

  const titleHtml = language === 'fr'
    ? 'Votre conversation est pr&ecirc;te &agrave; &ecirc;tre envoy&eacute;e par SMS!'
    : 'Your conversation is ready to be sent by SMS!';

  const phoneLabelHtml = language === 'fr'
    ? 'Num&eacute;ro de t&eacute;l&eacute;phone'
    : 'Phone number';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <MessageSquare className="w-5 h-5 text-primary" />
            <span dangerouslySetInnerHTML={{ __html: titleHtml }} />
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="sms-phone" dangerouslySetInnerHTML={{ __html: phoneLabelHtml }} />
            <div className="flex gap-2">
              <Input
                id="sms-phone"
                name="phone"
                type="tel"
                autoComplete="tel"
                inputMode="numeric"
                value={phone}
                onChange={handlePhoneChange}
                onKeyDown={handleKeyDown}
                placeholder="xxx-xxx-xxxx"
                className={`flex-1 text-base ${phoneError ? 'border-destructive' : ''}`}
                autoFocus
              />
              <Button
                type="button"
                size="icon"
                onClick={handleSubmit}
                disabled={isSubmitting || phone.replace(/\D/g, '').length !== 10}
                className="d2cbot-btn-square bg-primary hover:bg-primary/90"
                aria-label={isSubmitting ? (language === 'fr' ? 'Envoi...' : 'Sending...') : (language === 'fr' ? 'Envoyer' : 'Send')}
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
                )}
              </Button>
            </div>
            {phoneError && (
              <p 
                className="text-sm text-destructive" 
                dangerouslySetInnerHTML={{ __html: phoneError }}
              />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SMSScreenshotModal;
